import classNames from 'classnames';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import ReactCalendar from 'react-calendar';
import Spinner from '@/components/atoms/spinner/spinner';
import Typography from '@/components/atoms/typography/typography';
import FloatingTooltip from '@/components/molecules/floating-tooltip/floating-tooltip';
import variables from '@/components/organisms/calendar/calendar.variables';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { capitalizeFirstLetter } from '@/libraries/formatters/capitalize-first-letter';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import { getRangeEndDate } from '@/libraries/getters/get-range-end-date';
import CalendarButton from './calendar-button/calendar-button';
import CalendarOfferIcon from './calendar-offer-icon/calendar-offer-icon';
import type { CalendarDateAnnotation } from './calendar.types';
import styles from './calendar.module.scss';

type ValuePiece = Date | null;

type Value = ValuePiece | [ValuePiece, ValuePiece];

export interface CalendarProps {
  /** The class name to apply */
  className?: string;
  /** Whether to remove range styles */
  customRangeStyle?: boolean;
  /** A list of date annotations rendered as tooltips with custom tile styling */
  dateAnnotation?: CalendarDateAnnotation;
  /** Indicates the number of months to be displayed */
  displayedMonths?: number;
  /** A list of available dates that are highlighted in the calendar */
  highlightedDates?: Date[];
  /** Indicates whether the dropdown is in a loading state */
  isLoading?: boolean;
  /** Locale for the calendar labels */
  locale?: Locale;
  /** A list of low price dates to highlight in the calendar */
  lowPriceDates?: Date[];
  /** Maximum valid date for range */
  maxDate?: Date;
  /** Minimum valid date for range */
  minDate?: Date;
  /** A list of offers to show in the calendar */
  offers?: Date[];
  /** An event that is triggered on change */
  onChange?: (selectedDates: Date[]) => void;
  /** Indicates whether user can select a date range */
  rangeSelect?: boolean;
  /** Number of days that represents the length of the stay */
  rangeSize?: number;
  /** Preselected date in the calendar */
  selectedDate?: Date | null;
  /** Preselected date range end in the calendar */
  selectedMaxDate?: Date | null;
}

const Calendar = ({
  className,
  customRangeStyle = false,
  dateAnnotation = variables.dateAnnotation,
  displayedMonths = 2,
  highlightedDates = [],
  isLoading = false,
  locale = 'en',
  lowPriceDates = [],
  maxDate,
  minDate,
  offers = [],
  onChange,
  rangeSelect = false,
  rangeSize = 7,
  selectedDate = null,
  selectedMaxDate = null,
}: CalendarProps) => {
  const [selectedRange, setSelectedRange] = useState<[Date, Date] | null>(null);
  const { t } = useI18n();
  const { dateFormatter } = useDateFormatter();

  const isDateHighlighted = (date: Date) => {
    return highlightedDates.some(
      highlightedDate => date.toDateString() === highlightedDate.toDateString(),
    );
  };

  const isDateLowPrice = (date: Date) => {
    return lowPriceDates.some(
      lowPriceDate => date.toDateString() === lowPriceDate.toDateString(),
    );
  };

  const handleDateChange = (value: Value) => {
    if (rangeSelect && Array.isArray(value) && value.length === 2) {
      setSelectedRange(value as [Date, Date]);
      onChange && onChange(value as [Date, Date]);
    } else if (!rangeSelect && value instanceof Date) {
      if (!isDateHighlighted(value)) return;

      onChange && onChange([value]);
    }
  };

  const tileDisabled = ({ date }: { date: Date }): boolean => {
    if (rangeSelect) {
      return !!((minDate && date < minDate) || (maxDate && date > maxDate));
    }

    return !isDateHighlighted(date);
  };

  const hasAnnotationForDate = (date: Date): boolean => {
    const { endDate, startDate } = dateAnnotation?.range ?? {};

    if (!startDate || !endDate) return false;

    const day = new Date(date);

    day.setHours(0, 0, 0, 0);

    const start = new Date(startDate);

    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);

    end.setHours(0, 0, 0, 0);

    return day >= start && day <= end;
  };

  useEffect(() => {
    if (selectedDate === null) {
      setSelectedRange(null);

      return;
    }

    if (!rangeSelect) {
      const endDate = getRangeEndDate(selectedDate, rangeSize + 1);

      setSelectedRange([selectedDate, endDate]);
    } else if (selectedDate && selectedMaxDate) {
      setSelectedRange([selectedDate, selectedMaxDate]);
    }
  }, [rangeSelect, rangeSize, selectedDate, selectedMaxDate]);

  const defaultActiveStartDate = selectedDate
    ? selectedDate
    : !rangeSelect && highlightedDates.length > 0
      ? highlightedDates[0]
      : rangeSelect && minDate
        ? minDate
        : undefined;

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.hasCustomRangeStyle]: customRangeStyle,
      })}
    >
      {isLoading && (
        <Spinner
          className={styles.spinner}
          loadingText={t('items.search.searchBar.updatingSearch')}
        />
      )}

      <ReactCalendar
        className={styles.calendar}
        defaultActiveStartDate={defaultActiveStartDate}
        formatDay={(_, date) => dateFormatter(date).format('D')}
        formatMonthYear={(_, date) =>
          capitalizeFirstLetter(dateFormatter(date).format('MMMM YYYY'))
        }
        formatShortWeekday={(_, date) =>
          capitalizeFirstLetter(dateFormatter(date).format('dd'))
        }
        locale={locale}
        maxDate={rangeSelect ? maxDate : undefined}
        maxDetail="month"
        minDate={rangeSelect ? minDate : undefined}
        minDetail="month"
        next2Label={null}
        nextLabel={
          <CalendarButton
            aria-label={t('items.search.searchBar.date.nextMonth')}
            icon="ChevronRight"
          />
        }
        onChange={handleDateChange}
        prev2Label={null}
        prevLabel={
          <CalendarButton
            aria-label={t('items.search.searchBar.date.previousMonth')}
            icon="ChevronLeft"
          />
        }
        returnValue={rangeSelect ? 'range' : 'start'}
        selectRange={rangeSelect}
        showDoubleView={displayedMonths === 2}
        showFixedNumberOfWeeks={false}
        showWeekNumbers
        tileClassName={({ date }) => {
          const hasAnnotation = hasAnnotationForDate(date);

          return classNames({
            [styles.isHighlighted]: isDateHighlighted(date),
            ...(hasAnnotation && dateAnnotation?.styleModifier
              ? { [dateAnnotation.styleModifier]: true }
              : {}),
          });
        }}
        tileContent={({ date }) => {
          const hasLowPrice = isDateLowPrice(date);
          const hasOffer = offers?.find(
            offer =>
              dayjs(offer).format('YYYY-MM-DD') ===
              dayjs(date).format('YYYY-MM-DD'),
          );
          const hasAnnotation = hasAnnotationForDate(date);

          return (
            <div className={styles.tileContent}>
              {hasOffer && (
                <CalendarOfferIcon className={styles.offerIndicator} />
              )}

              {hasLowPrice && <span className={styles.lowPriceIndicator} />}

              {hasAnnotation && dateAnnotation && (
                <FloatingTooltip
                  className={styles.annotationTrigger}
                  headline={dateAnnotation.tooltipHeadline}
                  maxWidth={300}
                  text={dateAnnotation.tooltipText}
                />
              )}
            </div>
          );
        }}
        tileDisabled={tileDisabled}
        value={selectedRange}
        view="month"
      />

      {(lowPriceDates.length > 0 || offers.length > 0) && (
        <div className={styles.legend}>
          {offers.length > 0 && (
            <div className={styles.legendItem}>
              <CalendarOfferIcon />

              <Typography tagStyle="bodySmall">
                {t('items.common.words.campaignDeals')}
              </Typography>
            </div>
          )}

          {lowPriceDates.length > 0 &&
            t('items.common.words.cheapestDepartureDates') && (
              <div className={styles.legendItem}>
                <span className={classNames(styles.legendItemIcon)} />

                <Typography tagStyle="bodySmall">
                  {t('items.common.words.cheapestDepartureDates')}
                </Typography>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

Calendar.displayName = 'Calendar';

export default Calendar;
